import { put, select, takeEvery } from "redux-saga/effects";
import {
  BusinessAction,
  DefaultBuyerSplitAction,
  DefaultVendorSplitAction,
  updateModalContext,
} from "actions";
import { getEditBusinessRoute } from "lib/navigation";
import {
  OPEN_GENERATE_REPORTS_FROM_BUSINESS_TRANSACTIONS_TABLE,
  SUBMIT_BUSINESS_FORM,
  TOGGLE_BUSINESS_MODAL_SECTION,
} from "constants/actionTypes";
import { ModalTypes } from "constants/navigation";
import { getBusinessById, getContextByModalType } from "selectors";
import queryString from "query-string";
import history from "appHistory";
import { v4 as uuidv4 } from "uuid";
import { GenerateMode } from "components/MultiSaleReports";
import { SubmitFormAction } from "actions/modals/business";
import { openHashModalAndWait, storeFormikSnapshot } from "./util";
import { Reports } from "constants/reports";

function* onSubmitBusinessForm(action: SubmitFormAction) {
  const { businessId, submittedValues, touched } = action;

  const state = (yield select()) as State;

  // Use patchkeys from the modal to determine fields which were ready to patch when we
  // entered another modal
  const ctx = getContextByModalType(ModalTypes.EditBusiness)(state) || {};
  const patchKeysFromRestore: string[] = ctx?.persistentValues?.patchKeys || [];

  // Only patch the fields that have been touched.
  // Do so pretty bluntly - eg if properties is touched, submit the lot.
  const patchKeysFromTouched = Object.keys(touched || {}).filter(
    k => touched?.[k],
  );

  // build out keys that will be submitted - both from modal context and touched fields
  // eg: I edit notes -> open new modal -> come back and I edit name - both should be stored.
  const patchKeys = Array.from(
    new Set([...(patchKeysFromRestore || []), ...patchKeysFromTouched]),
  );

  const business = getBusinessById(businessId)(state);

  // Do a little munging of the submitted values to satisfy some form logic.
  if (submittedValues.defaultVendorSplit?.splits?.length === 0) {
    if (submittedValues.defaultVendorSplit.id) {
      yield put(
        DefaultVendorSplitAction.delete(submittedValues.defaultVendorSplit.id),
      );
    }
    delete submittedValues.defaultVendorSplit;
  }

  if (submittedValues.defaultBuyerSplit?.splits?.length === 0) {
    if (submittedValues.defaultBuyerSplit.id) {
      yield put(
        DefaultBuyerSplitAction.delete(submittedValues.defaultBuyerSplit.id),
      );
    }
    delete submittedValues.defaultBuyerSplit;
  }

  // if the primary contact email is not available in the
  // email recipients or business users anymore - remove it.
  if (submittedValues.primaryContactEmail) {
    const primaryContactIsEmailRecipient = submittedValues.emailRecipients.some(
      er => er.email === submittedValues.primaryContactEmail,
    );

    const primaryContactIsBusinessUser = submittedValues.businessUsers.some(
      bu => bu.email === submittedValues.primaryContactEmail,
    );

    if (!primaryContactIsEmailRecipient && !primaryContactIsBusinessUser) {
      submittedValues.primaryContactEmail = null;
    }
  }

  if (business) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic business values from form
    const values: Record<string, any> = { id: businessId };
    patchKeys.forEach(key => {
      values[key] = submittedValues[key];
    });

    if (submittedValues.generateBpayCRN && values.bpayCustomerReferenceNumber) {
      delete values.bpayCustomerReferenceNumber;
    }

    yield put(
      BusinessAction.update(values, {
        changeReason: "Updated via edit business.",
      }),
    );
  } else {
    // But submit everything on a create.
    yield put(
      BusinessAction.create({
        id: uuidv4(),
        ...submittedValues,
      }),
    );
  }
  yield put(
    updateModalContext(ModalTypes.EditBusiness, {
      persistentValues: { patchKeys: null },
    }),
  );
}

function* onOpenGenerateReportsFromBusinessTransactionsTable(action) {
  const { businessId, formikSnapshot } = action;

  const patchKeys = Object.keys(formikSnapshot?.touched || {}).filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Formik touched state dynamic keys
    k => (formikSnapshot?.touched as any)?.[k],
  );

  const snapshotId = yield storeFormikSnapshot(
    ModalTypes.EditBusiness,
    formikSnapshot,
  );

  yield* openHashModalAndWait(ModalTypes.MultiSaleReports, {
    allowedReports: [
      Reports.FinancialStatementReport.slug,
      Reports.FinancialStatementZipReport.slug,
      Reports.DetailedFinancialStatementReport.slug,
      Reports.OutstandingStatementReport.slug,
      Reports.TransactionListReportPdf.slug,
    ],
    generateMode: GenerateMode.SINGLE,
    businessId,
  });

  yield put(
    updateModalContext(ModalTypes.EditBusiness, {
      values: { snapshotId },
      persistentValues: { patchKeys },
    }),
  );
}

function* onToggleSection(action) {
  const state = yield select();

  const modelContext = getContextByModalType(ModalTypes.EditBusiness)(state);

  const currentSection = modelContext.section;

  // set the next section to be either the one requested, or null (close it) if it's already open
  const newSection = currentSection === action.section ? null : action.section;

  const hash = {
    ...queryString.parse(window.location.hash, {
      parseBooleans: true,
      parseNumbers: true,
      arrayFormat: "comma",
    }),
  };

  const currentArgs = JSON.parse(hash[ModalTypes.EditBusiness] as string);

  const nextHash = `#${getEditBusinessRoute(
    currentArgs.businessId,
    newSection,
    currentArgs.returnTo,
  )}`;

  history.push(nextHash);
}

export function* businessModalSagas() {
  yield takeEvery(SUBMIT_BUSINESS_FORM, onSubmitBusinessForm);
  yield takeEvery(
    OPEN_GENERATE_REPORTS_FROM_BUSINESS_TRANSACTIONS_TABLE,
    onOpenGenerateReportsFromBusinessTransactionsTable,
  );
  yield takeEvery(TOGGLE_BUSINESS_MODAL_SECTION, onToggleSection);
}
